import React, { useState, useEffect } from "react";
import "../styles/MatchingProfiles.css";
import { getJson, postJson } from "../network/api";
import { ApiEndpoints } from "../network/config";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

// 🔹 Select button dependencies
import Lottie from "lottie-react";
import { FaUserCheck } from "react-icons/fa";
import alreadyAnimation from "../assets/Already.json";
import doneAnimation from "../assets/Done.json";

const MatchingProfiles = () => {
  const navigate = useNavigate();
  const userId = sessionStorage.getItem("userId");

  const [profiles, setProfiles] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    limit: 20,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 🔹 Modal state (per card)
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // confirm | already | success
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const location = useLocation();
  const [pageInput, setPageInput] = useState("");

  // -----------------------------
  // Load Matching Profiles
  // -----------------------------
  const loadProfiles = async (page = 1) => {
    try {
      setLoading(true);
      setError("");

      const response = await getJson(ApiEndpoints.matchingProfiles, {
        params: {
          page,
          limit: 20,
          appUserId: userId,
        },
      });

      const records =
        response?.data?.records ||
        response?.data?.profiles ||
        response?.data ||
        response?.records ||
        response?.profiles ||
        [];

      setProfiles(Array.isArray(records) ? records : []);

      if (response?.pagination_data) {
        setPagination(response.pagination_data);
      } else {
        setPagination({
          page,
          pages: 1,
          limit: 20,
          total: records.length,
        });
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Failed to load matching profiles.");
      setProfiles([]);
      setPagination({ page: 1, pages: 1, limit: 20, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedPage =
      location.state?.page ||
      sessionStorage.getItem("matchingProfilesPage") ||
      1;

    loadProfiles(Number(savedPage));
  }, []);

  // -----------------------------
  // Auto close success/already modal
  // -----------------------------
  useEffect(() => {
    if (modalType === "success") {
      const t = setTimeout(() => {
        setShowModal(false);
        loadProfiles(pagination.page);
      }, 2000);
      return () => clearTimeout(t);
    }

    if (modalType === "already") {
      const t = setTimeout(() => setShowModal(false), 2000);
      return () => clearTimeout(t);
    }
  }, [modalType, pagination.page]);

  // -----------------------------
  // Select Profile Logic (SAME AS ProfileInfo)
  // -----------------------------
  const handleSelectProfile = async () => {
    try {
      const selectedRes = await getJson(ApiEndpoints.selectedProfiles, {
        params: {
          page: 1,
          limit: 9999,
          appUserId: userId,
        },
      });

      const selectedList = selectedRes?.data || [];

      const alreadySelected = selectedList.some(
        (p) =>
          p.id === selectedProfileId ||
          p.profileId === selectedProfileId
      );

      if (alreadySelected) {
        setModalType("already");
        return;
      }

      await postJson(
        ApiEndpoints.selectProfile,
        {},
        {
          params: {
            userId,
            profileId: selectedProfileId,
          },
        }
      );

      setModalType("success");
    } catch (error) {
      console.error("Select Profile Error:", error);
      setModalType("already");
    }
  };

  // -----------------------------
  // Pagination handlers
  // -----------------------------
  const handleFirst = () => loadProfiles(1);
  const handlePrev = () =>
    pagination.page > 1 && loadProfiles(pagination.page - 1);
  const handleNext = () =>
    pagination.page < pagination.pages &&
    loadProfiles(pagination.page + 1);
  const handleLast = () => loadProfiles(pagination.pages);

  const handlePageJump = () => {
    const pageNum = Number(pageInput);

    if (!pageNum || pageNum < 1 || pageNum > pagination.pages) return;

    loadProfiles(pageNum);
  };

  const getPageNumbers = () => {
    const totalPages = pagination.pages;
    const current = pagination.page;
    const delta = 2;

    const pages = [];

    const rangeStart = Math.max(2, current - delta);
    const rangeEnd = Math.min(totalPages - 1, current + delta);

    pages.push(1);

    if (rangeStart > 2) pages.push("...");

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    if (rangeEnd < totalPages - 1) pages.push("...");

    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  if (error) {
    return <p style={{ color: "red", textAlign: "center" }}>{error}</p>;
  }

  return (
    <div className="matching-page">
      <div className="matching-header-section">
        <h1 className="matching-main-title">Matching Profiles</h1>
      </div>

      {loading ? (
        <p className="loading-text">Loading...</p>
      ) : (
        <div className="matching-profile-container">
          {profiles.length === 0 ? (
            <p style={{ textAlign: "center", width: "100%" }}>
              No matching profiles found.
            </p>
          ) : (
            profiles.map((p) => (
              <div className="matching-profile-card" key={p.id || p.fileNo}>
                {p.profileImages?.[0]?.imageUrl && (
                  <img
                    src={p.profileImages[0].imageUrl}
                    alt={p.fileNo}
                    className="matching-profile-img"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                )}

                <div className="matching-profile-details">
                  <p>{p.fileNo}</p>
                  <p>{p.sgsNo}</p>

                  {/* 🔹 BUTTON ROW */}
                  <div className="matching-btn-row">
                    <button
                      className="matching-info-btn"
                      onClick={() => {
                        sessionStorage.setItem("matchingProfilesPage", pagination.page);

                        navigate("/matching-profile-info", {
                          state: {
                            profileId: p.id,
                            page: pagination.page,
                          },
                        });
                      }}
                    >
                      More Info
                    </button>

                    <button
                      className="select-profile-btn"
                      onClick={() => {
                        setSelectedProfileId(p.id);
                        setModalType("confirm");
                        setShowModal(true);
                      }}
                    >
                      <FaUserCheck style={{ marginRight: "6px" }} />
                      Select
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ---------------- MODAL ---------------- */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            {modalType === "confirm" && (
              <div className="modal-content">
                <p className="modal-message">
                  Are you sure you want to select this profile?
                </p>
                <div className="modal-buttons">
                  <button className="btn-yes" onClick={handleSelectProfile}>
                    Yes
                  </button>
                  <button
                    className="btn-no"
                    onClick={() => setShowModal(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            )}

            {modalType === "already" && (
              <div style={{ textAlign: "center" }}>
                <Lottie
                  animationData={alreadyAnimation}
                  loop={false}
                  style={{ width: 120, height: 120, margin: "0 auto" }}
                />
                <p>This profile is already selected.</p>
              </div>
            )}

            {modalType === "success" && (
              <div style={{ textAlign: "center" }}>
                <Lottie
                  animationData={doneAnimation}
                  loop={false}
                  style={{ width: 120, height: 120, margin: "0 auto" }}
                />
                <p>Profile selected successfully!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------- PAGINATION ---------------- */}

      {!loading && !error && profiles.length > 0 && (
        <div className="pagination-bar">

          {/* ROW 1 */}
          <div className="pagination-row">

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(1)}
              disabled={pagination.page === 1}
            >
              &#171;
            </button>

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              &#8592;
            </button>

            {getPageNumbers().map((p, index) =>
              p === "..." ? (
                <span key={index} className="pagination-dots">...</span>
              ) : (
                <button
                  key={p}
                  className={`pagination-btn ${p === pagination.page ? "active" : ""}`}
                  onClick={() => loadProfiles(p)}
                >
                  {p}
                </button>
              )
            )}

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
            >
              &#8594;
            </button>

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(pagination.pages)}
              disabled={pagination.page === pagination.pages}
            >
              &#187;
            </button>

          </div>

          {/* ROW 2 */}
          <div className="pagination-row jump-row">
            <span>Go to page:</span>

            <input
              type="number"
              min="1"
              max={pagination.pages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              className="page-input"
            />

            <button className="pagination-btn" onClick={handlePageJump}>
              Go
            </button>
          </div>

        </div>
      )}
    </div>

  );
};

export default MatchingProfiles;
